import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { paymentAPI, leaseAPI, tenantAPI, unitAPI, exportAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  DollarSign,
  Calendar,
  User,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Download,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const Payments = () => {
  const { t } = useI18n();
  const [payments, setPayments] = useState([]);
  const [leases, setLeases] = useState([]);
  const [tenantMap, setTenantMap] = useState({});
  const [unitMap, setUnitMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    lease_id: '',
    amount: '',
    due_date: '',
    description: 'Monthly Rent'
  });

  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await exportAPI.payments();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'payments_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Payments exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleGenerateMonthly = async () => {
    setGenerating(true);
    try {
      const res = await paymentAPI.generateMonthly();
      const { created, skipped } = res.data;
      if (created > 0) {
        toast.success(`Generated ${created} payment(s)${skipped > 0 ? `, ${skipped} already existed` : ''}`);
      } else {
        toast.info('All monthly payments already exist for this period');
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate payments');
    } finally {
      setGenerating(false);
    }
  };

  const loadData = async () => {
    try {
      const [paymentsRes, leasesRes, tenantsRes, unitsRes] = await Promise.all([
        paymentAPI.getAll(),
        leaseAPI.getAll(),
        tenantAPI.getAll(),
        unitAPI.getAll(),
      ]);
      setPayments(paymentsRes.data);
      setLeases(leasesRes.data.filter(l => l.status === 'ACTIVE'));
      const tMap = {};
      tenantsRes.data.forEach(t => { tMap[t.id] = t.name; });
      setTenantMap(tMap);
      const uMap = {};
      unitsRes.data.forEach(u => { uMap[u.id] = u.unit_number; });
      setUnitMap(uMap);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      await paymentAPI.create(submitData);
      toast.success('Payment request created');
      setDialogOpen(false);
      setFormData({
        lease_id: '',
        amount: '',
        due_date: '',
        description: 'Monthly Rent'
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAID': return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'OVERDUE': return <AlertTriangle className="w-5 h-5 text-red-400" />;
      default: return <Clock className="w-5 h-5 text-amber-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'OVERDUE': return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'PENDING': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const totalPending = payments
    .filter(p => p.status === 'PENDING' || p.status === 'OVERDUE')
    .reduce((sum, p) => sum + p.amount + (p.late_fee || 0), 0);

  const totalCollected = payments
    .filter(p => p.status === 'PAID')
    .reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('payments.title')}>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="landlord" title="Payments">
      <div className="space-y-6" data-testid="payments-page">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Pending Revenue</p>
                  <p className="text-2xl font-semibold text-amber-400">${totalPending.toLocaleString()}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Collected</p>
                  <p className="text-2xl font-semibold text-emerald-400">${totalCollected.toLocaleString()}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Requests</p>
                  <p className="text-2xl font-semibold">{payments.length}</p>
                </div>
                <DollarSign className="w-8 h-8 text-gold opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">{t('payments.subtitle')}</p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-border text-muted-foreground hover:text-foreground"
              onClick={handleExportCSV}
              disabled={exporting || payments.length === 0}
              data-testid="export-payments-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              variant="outline"
              className="border-gold/30 text-gold hover:bg-gold/10"
              onClick={handleGenerateMonthly}
              disabled={generating || leases.length === 0}
              data-testid="generate-monthly-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              {generating ? 'Generating...' : 'Generate Monthly'}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gold text-white hover:bg-gold-light"
                disabled={leases.length === 0}
                data-testid="create-payment-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Payment Request
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[400px] bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif">Create Payment Request</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Lease</Label>
                  <Select 
                    value={formData.lease_id} 
                    onValueChange={(v) => {
                      const lease = leases.find(l => l.id === v);
                      setFormData({
                        ...formData, 
                        lease_id: v,
                        amount: lease?.monthly_rent || ''
                      });
                    }}
                  >
                    <SelectTrigger className="bg-background border-[#e5e7eb]" data-testid="payment-lease-select">
                      <SelectValue placeholder="Select lease" />
                    </SelectTrigger>
                    <SelectContent>
                      {leases.map(l => (
                        <SelectItem key={l.id} value={l.id}>
                          Unit {unitMap[l.unit_id] || '?'} &mdash; {tenantMap[l.tenant_id] || 'Tenant'} &mdash; ${l.monthly_rent}/mo
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Amount ($)</Label>
                  <Input
                    name="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={handleChange}
                    className="bg-background border-[#e5e7eb]"
                    required
                    data-testid="payment-amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Due Date</Label>
                  <Input
                    name="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={handleChange}
                    className="bg-background border-[#e5e7eb]"
                    required
                    data-testid="payment-due-date"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">Description</Label>
                  <Input
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="bg-background border-[#e5e7eb]"
                    placeholder="Monthly Rent"
                    data-testid="payment-description-input"
                  />
                </div>

                <Button type="submit" className="w-full bg-gold text-white hover:bg-gold-light" data-testid="save-payment-btn">
                  Create Payment Request
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Payments List */}
        {payments.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">No Payment Requests</h3>
              <p className="text-muted-foreground mb-6">
                Create payment requests for your tenants
              </p>
              {leases.length === 0 ? (
                <p className="text-sm text-amber-400">You need active leases to create payment requests</p>
              ) : (
                <Button onClick={() => setDialogOpen(true)} className="bg-gold text-white hover:bg-gold-light">
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Payment Request
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {payments.map((payment, index) => (
              <Card 
                key={payment.id} 
                className="bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`payment-card-${payment.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(payment.status)}
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-medium">{payment.description}</h3>
                          <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getStatusColor(payment.status)}`}>
                            {payment.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                          {tenantMap[payment.tenant_id] && (
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {tenantMap[payment.tenant_id]}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Due: {payment.due_date}
                          </span>
                          {payment.paid_date && (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Paid: {payment.paid_date}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-2xl font-semibold">
                        ${payment.amount.toLocaleString()}
                      </p>
                      {payment.late_fee > 0 && (
                        <p className="text-sm text-red-400">
                          + ${payment.late_fee.toLocaleString()} late fee
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Payments;
